import type { AiTrendAnalysis } from '../../types/styling-analysis.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business Display AI-generated multi-dimensional request trend analysis: summary, themes, persona insights, item preferences, and actionable insights.
 * @graph-connects none
 */
export function AiTrendAnalysisPanel({
  analysis,
  requestCount,
}: {
  analysis: AiTrendAnalysis;
  requestCount: number;
}) {
  return (
    <div className="space-y-8">
      <section>
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">AI分析サマリ</h3>
          <span className="rounded-full bg-[var(--color-accent-primary)]/10 px-2 py-0.5 text-xs text-[var(--color-accent-primary)]">
            {requestCount}件のリクエストを分析
          </span>
        </div>
        <p className="mt-2 text-sm leading-relaxed text-[var(--color-text-secondary)]">
          {analysis.summary}
        </p>
      </section>

      {analysis.themes.length > 0 && (
        <section>
          <h3 className="mb-3 text-sm font-semibold text-[var(--color-text-primary)]">
            要望テーマ分類
          </h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {analysis.themes.map((theme) => (
              <div key={theme.label} className="rounded-lg border border-[var(--color-border)] p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[var(--color-text-primary)]">
                    {theme.label}
                  </span>
                  <span className="rounded-full bg-[var(--color-bg-elevated)] px-2 py-0.5 text-xs text-[var(--color-text-muted)]">
                    {theme.count}件
                  </span>
                </div>
                <p className="mt-1 text-xs leading-relaxed text-[var(--color-text-secondary)]">
                  {theme.description}
                </p>
                {theme.representativeQuotes.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {theme.representativeQuotes.map((quote, i) => (
                      <p
                        key={i}
                        className="border-l-2 border-[var(--color-accent-primary)]/30 pl-2 text-xs text-[var(--color-text-muted)] italic"
                      >
                        {quote}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {analysis.personaInsights.length > 0 && (
        <section>
          <h3 className="mb-3 text-sm font-semibold text-[var(--color-text-primary)]">
            ペルソナ別傾向
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-xs text-[var(--color-text-muted)]">
                  <th className="px-3 py-2">セグメント</th>
                  <th className="px-3 py-2">特徴的な要望パターン</th>
                  <th className="px-3 py-2 text-right">件数</th>
                </tr>
              </thead>
              <tbody>
                {analysis.personaInsights.map((insight) => (
                  <tr
                    key={insight.segmentLabel}
                    className="border-b border-[var(--color-border)] last:border-b-0"
                  >
                    <td className="whitespace-nowrap px-3 py-2 font-medium text-[var(--color-text-primary)]">
                      {insight.segmentLabel}
                    </td>
                    <td className="px-3 py-2 text-[var(--color-text-secondary)]">
                      {insight.characteristics}
                    </td>
                    <td className="px-3 py-2 text-right text-[var(--color-text-muted)]">
                      {insight.count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section>
        <h3 className="mb-3 text-sm font-semibold text-[var(--color-text-primary)]">
          アイテム嗜好
        </h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <PreferenceList
            label="希望カテゴリ"
            items={analysis.itemPreferences.wantedCategories}
            variant="positive"
          />
          <PreferenceList
            label="NGカテゴリ"
            items={analysis.itemPreferences.avoidedCategories}
            variant="negative"
          />
          <PreferenceList
            label="色の傾向"
            items={analysis.itemPreferences.colorPreferences}
            variant="neutral"
          />
          <PreferenceList
            label="シルエット傾向"
            items={analysis.itemPreferences.silhouettePreferences}
            variant="neutral"
          />
        </div>
      </section>

      {analysis.actionableInsights.length > 0 && (
        <section>
          <h3 className="mb-3 text-sm font-semibold text-[var(--color-text-primary)]">
            アクション提案
          </h3>
          <ul className="space-y-2">
            {analysis.actionableInsights.map((insight, i) => (
              <li
                key={i}
                className="flex gap-2 rounded-md bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-text-secondary)]"
              >
                <span className="shrink-0 text-[var(--color-accent-primary)]">{i + 1}.</span>
                {insight}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/** @graph-connects none */
const VARIANT_STYLES = {
  positive: 'bg-green-500/10 text-green-700 dark:text-green-400',
  negative: 'bg-red-500/10 text-red-700 dark:text-red-400',
  neutral: 'bg-[var(--color-accent-primary)]/10 text-[var(--color-accent-primary)]',
} as const;

/** @graph-connects none */
function PreferenceList({
  label,
  items,
  variant,
}: {
  label: string;
  items: string[];
  variant: 'positive' | 'negative' | 'neutral';
}) {
  if (items.length === 0) return null;

  return (
    <div>
      <span className="text-xs font-medium text-[var(--color-text-muted)]">{label}</span>
      <div className="mt-1 flex flex-wrap gap-1">
        {items.map((item) => (
          <span
            key={item}
            className={`rounded-full px-2 py-0.5 text-xs ${VARIANT_STYLES[variant]}`}
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}
